import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';

import LoggingService from 'components/core/logging/LoggingService';
import { type Intl, translate } from 'utils/intlUtils';

export interface ApiError {
  extensions?: {
    classification?: string;
    debug?: string;
    fields: string[];
    status?: number;
  };
  message?: string;
  name?: string;
}

export default interface ApiErrors {
  errors: ApiError[];
}

/**
 * Get an array of errors from an api response.
 *
 * Handles Apollo v4 error formats:
 * - CombinedGraphQLErrors (HTTP 200 with errors): `e.errors`
 * - ServerError (HTTP 4xx/5xx): parsed from `e.bodyText`
 */
export const getApiErrors = (e: unknown): ApiError[] => {
  if (CombinedGraphQLErrors.is(e)) {
    return e.errors.map(({ extensions, message }) => ({
      extensions: extensions as ApiError['extensions'],
      message,
    }));
  }

  if (ServerError.is(e) && typeof e.bodyText === 'string') {
    try {
      const parsed = JSON.parse(e.bodyText) as {
        errors?: ApiError[];
      };
      if (Array.isArray(parsed?.errors)) {
        return parsed.errors;
      }
    } catch {
      return [];
    }
  }

  return [];
};

export const getValidApiErrorObject = (errors: any) =>
  (Array.isArray(errors) ? errors : getApiErrors(errors))?.find?.(error => error?.message);

export const getValidApiErrorObjects = (errors: any) =>
  (Array.isArray(errors) ? errors : getApiErrors(errors))?.filter?.(error => error?.message);

export const logApiDebugInfo = (errors: ApiError[]) => {
  if (process.env.REACT_APP_FEATURE_ENV_DEBUGGING !== 'true') {
    return;
  }

  const errorsWithDebug = errors.filter(error => error?.extensions?.debug);

  if (errorsWithDebug.length === 0) {
    return;
  }

  // eslint-disable-next-line no-console
  console.group(`🔍 API Debug Info (${errorsWithDebug.length} error${errorsWithDebug.length > 1 ? 's' : ''})`);

  for (let index = 0; index < errorsWithDebug.length; index++) {
    const error = errorsWithDebug[index];
    // eslint-disable-next-line no-console
    console.debug(
      `%c🚨 Debug Details ${errorsWithDebug.length > 1 ? `#${index + 1}` : ''}:`,
      'background: #ff4444; color: white; font-weight: bold; padding: 4px 8px; border-radius: 3px;',
      error.extensions!.debug
    );
    if (errorsWithDebug.length > 1 && index < errorsWithDebug.length - 1) {
      // eslint-disable-next-line no-console
      console.log('─'.repeat(50));
    }
  }

  // eslint-disable-next-line no-console
  console.groupEnd();
};

export const getApiErrorsMessage = (errors: any, fallbackMessage?: Intl): string =>
  getValidApiErrorObject(errors)?.message || translate.t(fallbackMessage || 'error_loading');

export const getApiErrorsMessages = (errors: any, fallbackMessage?: Intl): string[] => {
  const validErrors = getValidApiErrorObjects(errors);

  return validErrors.length > 1 ? validErrors.map(e => e.message) : [getApiErrorsMessage(errors, fallbackMessage)];
};

export interface ErrorNotificationContent {
  /**
   * Detail messages, deduplicated. When the errors include a specific
   * `extensions.debug` string, those are listed here; otherwise the generic
   * `message` fields are used.
   */
  messages: string[];
}

/**
 * Extract a user-facing message list from an unknown error for display in a
 * notification. Prefers `extensions.debug` (the specific reason) over the
 * generic localized `message`; supports multi-error responses by listing each
 * distinct detail. Falls back to `Error.message` and then the `error_loading`
 * translation.
 */
export const getErrorNotificationContent = (error: unknown): ErrorNotificationContent => {
  const apiErrors = getApiErrors(error);

  if (apiErrors.length === 0) {
    const message = error instanceof Error && error.message ? error.message : translate.t('error_loading');

    return { messages: [message] };
  }

  const debugs = apiErrors.map(apiError => apiError.extensions?.debug).filter((debug): debug is string => !!debug);

  if (debugs.length > 0) {
    return { messages: [...new Set(debugs)] };
  }

  const messages = apiErrors.map(apiError => apiError.message).filter((message): message is string => !!message);

  return { messages: messages.length > 0 ? [...new Set(messages)] : [translate.t('error_loading')] };
};

/**
 * Used to centralize and log api error information.
 *
 * Needed so that monitoring services can fingerprint and group errors accordingly.
 * The debug error message is a non-localized developer-only error message
 * which is used by default for fingerprinting.
 *
 * @param errors An error or array of errors from api.
 * @param customMessage Custom error message to override the api error message.
 */
export const logApiError = (errors: any, customMessage?: string) => {
  const validApiError = getValidApiErrorObject(errors);
  const apiDebugErrorMessage = validApiError?.extensions?.debug;
  const errorClassification = validApiError?.extensions?.classification;
  const errorObject = Array.isArray(errors) ? undefined : errors;
  const errorMessage = errors?.message || '';

  const fingerprintMessage = `LogApiError: ${
    customMessage || apiDebugErrorMessage || errorClassification || 'Unknown API error.'
  }${errorMessage && ` - ${errorMessage}`}`;
  LoggingService.error({
    error: errorObject,
    message: fingerprintMessage,
    messageContext: errors,
  });
};
